import { Icon } from "@cadmakers-ui/icons";
import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { Button, Input, Table } from "reactstrap";
import { StatusCode } from "../../App/Enums/StatusCode";
import ResponseHandler from "../../App/ResponseHandler";
import { ApiRequest, ErrorDisplay, IAppState, LoadingDisplay } from "../../Common";
import { LoadingState } from "../../Common/Models/LoadingState";
import { ResponseError } from "../../Common/Models/ResponseError";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import { IconButton, Modal, ModalHeader, ModalBody, ModalFooter } from "../../Layout";
import { endpoints } from "../Constants/endpoints";
import { Topic } from "../Models/Topic";
import { TopicManagerAssignment } from "../Models/TopicManagerAssignment";
import { TopicUserAutoCompleteItem } from "../Models/TopicUserAutoCompleteItem";
import { TopicUsersAutocomplete } from "./TopicUsersAutocomplete";
import { TopicUsersAutocompleteType } from "../Enums/TopicUsersAutocompleteType";
import {
  getTopicManagers,
  setLoadingTopicManagers,
  setTopicManagers,
  setTopicManagersError
} from "../../Startup/Actions/StartupActions";
import iconStyles from "../../Common/Styles/Icons.module.scss";

type Props = {
  topic: Topic;
  toggle: boolean;
  onToggle: () => void;
};

type StateProps = {
  managerLoadState?: LoadingState;
  managerLoadError?: ResponseError;
  managers?: TopicManagerAssignment[];
};

type State = {
  filter: string;
  lock: boolean;
};
type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
  setLoadingTopicManagers: (topicCode: string) => void;
  setTopicManagers: (topicCode: string, managers: TopicManagerAssignment[]) => void;
  getTopicManagers: (code: string) => void;
};

const mapStateToProps = (state: IAppState, props: Props): StateProps => {
  const selectedTopicCode = props.topic != null ? props.topic.code : null;

  if (selectedTopicCode == null) {
    return {};
  }

  return {
    managerLoadState: state.app.topicManagersLoadingState[selectedTopicCode],
    managerLoadError: state.app.topicManagersError[selectedTopicCode],
    managers: state.app.topicManagers[selectedTopicCode]
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args)),
  setLoadingTopicManagers: (topicCode: string) => dispatch(setLoadingTopicManagers(topicCode)),
  setTopicManagers: (topicCode: string, managers: TopicManagerAssignment[]) =>
    dispatch(setTopicManagers(topicCode, managers)),
  getTopicManagers: (code: string) => dispatch(getTopicManagers(code))
});

class AssignTopicManagersModal extends React.Component<Props & StateProps & DispatchProps, State> {
  constructor(props: Props & DispatchProps) {
    super(props);

    this.state = {
      filter: "",
      lock: false
    };
  }

  componentDidUpdate = (prevProps: Props) => {
    // only load managers if this modal has just been opened
    if (prevProps.toggle !== this.props.toggle && this.props.toggle) {
      this.ensureManagersLoaded();
    }
  };

  ensureManagersLoaded = () => {
    if (
      !this.props.managerLoadState ||
      (!this.props.managerLoadState.isLoading &&
        !this.props.managerLoadState.isLoaded &&
        !this.props.managerLoadState.errored)
    ) {
      this.props.setLoadingTopicManagers(this.props.topic.code);
      this.props.getTopicManagers(this.props.topic.code);
    }
  };

  claimLock = () => {
    this.setState({
      lock: true
    });
  };

  releaseLock = () => {
    this.setState({
      lock: false
    });
  };

  onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filter: event.currentTarget.value
    });
  };

  onSelectUser = (user: TopicUserAutoCompleteItem) => {
    if (!this.state.lock) {
      this.claimLock();
      ApiRequest.fromPath(endpoints.topics.assignManager(this.props.topic.code))
        .withAuthHeader()
        .withJson({ username: user.username })
        .post()
        .then(response => {
          ResponseHandler.handle(
            response,
            () => {
              if (
                !!this.props.managers &&
                this.props.managers.find(a => a.user.username === user.username)
              ) {
                return;
              }

              this.props.setTopicManagers(this.props.topic.code, [
                ...(this.props.managers ? this.props.managers : []),
                {
                  user: {
                    id: user.userId,
                    name: user.name,
                    email: user.email,
                    username: user.username
                  },
                  allowStateChange: false
                } as TopicManagerAssignment
              ]);
            },
            result => {
              this.setError("Failed to assign user", result.error.message, response.status, true);
            },
            () => {
              this.setError(
                "Failed to assign user",
                "An unknown error has occured",
                response.status,
                true
              );
            }
          ).catch(() =>
            this.setError(
              "Failed to assign user",
              "Failed to parse JSON response",
              StatusCode.Status400BadRequest,
              true
            )
          );
        })
        .catch(() => {
          this.setError("Failed to assign user", "Failed to contact server", undefined, true);
        })
        .finally(() => {
          this.releaseLock();
        });
    }
  };

  removeManager = (username: string) => {
    if (!this.state.lock) {
      this.claimLock();

      ApiRequest.fromPath(endpoints.topics.unassignManager(this.props.topic.code))
        .withJson({ username })
        .withAuthHeader()
        .delete()
        .then(response => {
          ResponseHandler.handle(
            response,
            () => {
              this.props.setTopicManagers(
                this.props.topic.code,
                this.props.managers
                  ? this.props.managers.filter(a => a.user.username !== username)
                  : []
              );
            },
            result => {
              this.setError("Failed to remove user", result.error.message, response.status, true);
            },
            () => {
              this.setError(
                "Failed to remove user",
                "An unknown error has occured",
                response.status,
                true
              );
            }
          ).catch(() => {
            this.setError(
              "Failed to assign user",
              "Failed to parse JSON response",
              StatusCode.Status400BadRequest,
              true
            );
          });
        })
        .catch(() => {
          this.setError("Failed to remove user", "Failed to contact server", undefined, true);
        })
        .finally(() => this.releaseLock());
    }
  };

  setError = (
    result: string,
    description: string,
    code?: number,
    shouldShowSnack: boolean = false
  ) => {
    if (shouldShowSnack) {
      this.showErrorSnackbar(result, description, code);
    }
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    const label = `${reason}: ${errorDescription} ${errorCode ? `(Error Code ${errorCode})` : ""}`;

    this.props.showSnack("ASSIGN_TOPIC_MANAGERS_MODAL", {
      label,
      timeout: 5000
    });
  };

  renderModalBody = () => {
    if (!this.props.managerLoadState || this.props.managerLoadState.isLoading) {
      return <LoadingDisplay />;
    }

    if (this.props.managerLoadState.errored) {
      if (!this.props.managerLoadError) {
        return (
          <ErrorDisplay
            errorDescription="Error message not specified"
            errorResult="Could not load managers"
          />
        );
      }

      return (
        <ErrorDisplay
          errorCode={this.props.managerLoadError.errorCode}
          errorDescription={this.props.managerLoadError.errorDescription}
          errorResult={this.props.managerLoadError.errorResult}
        />
      );
    }

    const filteredAssignments = this.props.managers
      ? this.props.managers.filter(a =>
          a.user.name.toLowerCase().includes(this.state.filter.toLowerCase())
        )
      : [];

    return (
      <>
        <TopicUsersAutocomplete
          topic={this.props.topic}
          placeHolder="Search users"
          onItemSelect={this.onSelectUser}
          ignoreExistingUsers
          autocompleteFor={TopicUsersAutocompleteType.Manager}
        />
        <br />
        <Input
          type="text"
          placeholder="Filter assigned users"
          value={this.state.filter}
          onChange={this.onFilterChange}
        />
        <Table>
          <thead>
            <tr>
              <th>Assigned Users</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredAssignments.length > 0 ? (
              filteredAssignments.map(assignment => (
                <tr key={assignment.user.username}>
                  <td>{assignment.user.name}</td>
                  <td style={{ textAlign: "right" }}>
                    <IconButton
                      icon={
                        <Icon
                          appName="GeneralLayout"
                          assetName="GENERAL_L_DeleteTrash"
                          className={iconStyles.smallIcon}
                        />
                      }
                      onClick={() => this.removeManager(assignment.user.username)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: "center" }}>
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </>
    );
  };

  render() {
    return (
      <Modal isOpen={this.props.toggle} onToggle={this.props.onToggle}>
        <ModalHeader toggle={this.props.onToggle}>Assign Topic Managers</ModalHeader>
        <ModalBody onClick={e => e.stopPropagation()}>{this.renderModalBody()}</ModalBody>
        <ModalFooter>
          <Button onClick={this.props.onToggle}>Done</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AssignTopicManagersModal);
